import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ProjectState } from './models';
import { baseApi } from '@centerpiece/components';

export const project_state = baseApi
  .enhanceEndpoints({ addTagTypes: ['ProjectStates', 'ProjectState'] })
  .injectEndpoints({
    endpoints: builder => ({
      getProjectStates: builder.query<ProjectState[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'project_state',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'ProjectState' as const,
                  id,
                })),
                'ProjectStates',
              ]
            : ['ProjectStates'],
      }),
      getProjectState: builder.query<ProjectState, string>({
        query: id => ({
          url: `project_state/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'ProjectState', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createProjectState: builder.mutation({
        query: request => ({
          url: 'project_state',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['ProjectStates'],
      }),
      updateProjectState: builder.mutation<ProjectState, ProjectState>({
        query: request => ({
          url: `project_state/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'ProjectState', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            project_state.util.updateQueryData(
              'getProjectState',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(project_state.util.invalidateTags(['ProjectStates']))
             */
            dispatch(project_state.util.invalidateTags(['ProjectStates']));
          }
        },
      }),
      deleteProjectState: builder.mutation<ProjectState, number>({
        query: id => ({
          url: `project_state/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'ProjectState' as const, arg }, 'ProjectStates']
            : ['ProjectStates'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetProjectStatesQuery,
  useGetProjectStateQuery,
  useCreateProjectStateMutation,
  useUpdateProjectStateMutation,
  useDeleteProjectStateMutation,
  usePrefetch: useProjectStatesPrefetch,
} = project_state;
const selectProjectStatesRoot = project_state.endpoints.getProjectStates.select(
  {}
);
export const selectProjectStates = createSelector(
  selectProjectStatesRoot,
  project_state => project_state.data || []
);
